// src/pages/UserManagement.js - Complete with activeTab
import React, { useState, useEffect } from 'react';
import axios from 'axios';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ✅ THIS IS THE MISSING STATE
  const [activeTab, setActiveTab] = useState('users');
  
  // User modals
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    password: '',
    role_id: '',
    is_active: true
  });
  
  // Role modals
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });
  const [permissionString, setPermissionString] = useState('');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setUsers(response.data.users || []);
      setError(null);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/roles`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // ==================== USER FUNCTIONS ====================

  const handleCreateUser = async () => {
    if (!userFormData.full_name || !userFormData.phone_number || !userFormData.password) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/users`, userFormData, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('User created successfully!');
      setShowCreateUserModal(false);
      setUserFormData({ full_name: '', phone_number: '', email: '', password: '', role_id: '', is_active: true });
      fetchUsers();
    } catch (error) {
      alert('Failed to create user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/users/${selectedUser.id}`, userFormData, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('User updated successfully!');
      setShowEditUserModal(false);
      setSelectedUser(null);
      setUserFormData({ full_name: '', phone_number: '', email: '', password: '', role_id: '', is_active: true });
      fetchUsers();
    } catch (error) {
      alert('Failed to update user: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('User deleted successfully!');
      fetchUsers();
    } catch (error) {
      alert('Failed to delete user: ' + (error.response?.data?.error || error.message));
    }
  };

  const openEditUserModal = (user) => {
    setSelectedUser(user);
    setUserFormData({
      full_name: user.full_name || '',
      phone_number: user.phone_number || '',
      email: user.email || '',
      password: '',
      role_id: user.role_id || '',
      is_active: user.is_active !== false
    });
    setShowEditUserModal(true);
  };

  // ==================== ROLE FUNCTIONS ====================

  const handleCreateRole = async () => {
    if (!roleFormData.name) {
      alert('Role name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/roles`, {
        name: roleFormData.name,
        description: roleFormData.description,
        permissions: permissionString ? JSON.parse(permissionString) : {}
      }, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('Role created successfully!');
      setShowCreateRoleModal(false);
      setRoleFormData({ name: '', description: '', permissions: {} });
      setPermissionString('');
      fetchRoles();
    } catch (error) {
      alert('Failed to create role: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedRole) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/roles`, {
        name: roleFormData.name,
        description: roleFormData.description,
        permissions: permissionString ? JSON.parse(permissionString) : {}
      }, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('Role updated successfully!');
      setShowEditRoleModal(false);
      setSelectedRole(null);
      setRoleFormData({ name: '', description: '', permissions: {} });
      setPermissionString('');
      fetchRoles();
    } catch (error) {
      alert('Failed to update role: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This will remove it from all users.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/roles/${roleId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      alert('Role deleted successfully!');
      fetchRoles();
      fetchUsers();
    } catch (error) {
      alert('Failed to delete role: ' + (error.response?.data?.error || error.message));
    }
  };

  const openEditRoleModal = (role) => {
    setSelectedRole(role);
    setRoleFormData({
      name: role.name || '',
      description: role.description || '',
      permissions: role.permissions || {}
    });
    setPermissionString(JSON.stringify(role.permissions || {}, null, 2));
    setShowEditRoleModal(true);
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : 'No Role';
  };

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'super_admin': '#8b5cf6',
      'admin': '#f97316',
      'operations': '#3b82f6',
      'customer_service': '#10b981',
      'marketing': '#ec4899'
    };
    return colors[roleName] || '#64748b';
  };

  const getPermissionsSummary = (permissions) => {
    if (!permissions || Object.keys(permissions).length === 0) return 'No permissions';
    const keys = Object.keys(permissions);
    return keys.join(', ');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>👥 User & Role Management</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b' }}>
            <span>👤 {users.length} users</span>
            <span>🎭 {roles.length} roles</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              setUserFormData({ full_name: '', phone_number: '', email: '', password: '', role_id: '', is_active: true });
              setShowCreateUserModal(true);
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            ➕ Create User
          </button>
          <button
            onClick={() => {
              setRoleFormData({ name: '', description: '', permissions: {} });
              setPermissionString('');
              setShowCreateRoleModal(true);
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            ➕ Create Role
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* ✅ TABS - This uses the activeTab state */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'users' ? '#f97316' : 'transparent',
            color: activeTab === 'users' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'users' ? '600' : '400',
          }}
        >
          👤 Users
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'roles' ? '#f97316' : 'transparent',
            color: activeTab === 'roles' ? 'white' : '#64748b',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: activeTab === 'roles' ? '600' : '400',
          }}
        >
          🎭 Roles
        </button>
      </div>

      {/* USERS TAB */}
      {activeTab === 'users' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'auto',
        }}>
          {users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>👤</div>
              <p style={{ color: '#64748b', fontSize: '16px' }}>No admin users found</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Phone</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {user.is_master && '👑 '}{user.full_name}
                    </td>
                    <td style={{ padding: '12px' }}>{user.phone_number}</td>
                    <td style={{ padding: '12px' }}>{user.email || '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        backgroundColor: getRoleBadgeColor(getRoleName(user.role_id)) + '20',
                        color: getRoleBadgeColor(getRoleName(user.role_id)),
                        fontWeight: '500',
                      }}>
                        {getRoleName(user.role_id)}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        backgroundColor: user.is_active ? '#dcfce7' : '#fee2e2',
                        color: user.is_active ? '#166534' : '#991b1b',
                        fontWeight: '500',
                      }}>
                        {user.is_active ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => openEditUserModal(user)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '6px',
                        }}
                      >
                        ✏️ Edit
                      </button>
                      {!user.is_master && (
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ROLES TAB */}
      {activeTab === 'roles' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'auto',
        }}>
          {roles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎭</div>
              <p style={{ color: '#64748b', fontSize: '16px' }}>No roles found</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Role Name</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Description</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Permissions</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Users</th>
                  <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => {
                  const userCount = users.filter(u => u.role_id === role.id).length;
                  const isSystemRole = ['super_admin', 'admin', 'operations', 'customer_service', 'marketing'].includes(role.name);
                  
                  return (
                    <tr key={role.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px', fontWeight: '500' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          backgroundColor: getRoleBadgeColor(role.name) + '20',
                          color: getRoleBadgeColor(role.name),
                          fontWeight: '500',
                        }}>
                          {role.name}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#64748b' }}>{role.description || '—'}</td>
                      <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                        {getPermissionsSummary(role.permissions)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          backgroundColor: userCount > 0 ? '#dcfce7' : '#f1f5f9',
                          color: userCount > 0 ? '#166534' : '#64748b',
                        }}>
                          {userCount}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => openEditRoleModal(role)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            marginRight: '6px',
                          }}
                        >
                          ✏️ Edit
                        </button>
                        {!isSystemRole && userCount === 0 && (
                          <button
                            onClick={() => handleDeleteRole(role.id, role.name)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ==================== MODALS ==================== */}
      {/* (Keep all the modal code as before) */}

      {/* Create User Modal */}
      {showCreateUserModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 20px 0' }}>Create New Admin User</h3>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Full Name *</label>
              <input
                type="text"
                value={userFormData.full_name}
                onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Phone Number *</label>
              <input
                type="text"
                value={userFormData.phone_number}
                onChange={(e) => setUserFormData({ ...userFormData, phone_number: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Password *</label>
              <input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Role</label>
              <select
                value={userFormData.role_id}
                onChange={(e) => setUserFormData({ ...userFormData, role_id: parseInt(e.target.value) || '' })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={userFormData.is_active}
                  onChange={(e) => setUserFormData({ ...userFormData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateUserModal(false)}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                style={primaryButtonStyle}
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 20px 0' }}>Edit Admin User</h3>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Full Name *</label>
              <input
                type="text"
                value={userFormData.full_name}
                onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Phone Number *</label>
              <input
                type="text"
                value={userFormData.phone_number}
                onChange={(e) => setUserFormData({ ...userFormData, phone_number: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>New Password (optional)</label>
              <input
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                placeholder="Leave blank to keep current"
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Role</label>
              <select
                value={userFormData.role_id}
                onChange={(e) => setUserFormData({ ...userFormData, role_id: parseInt(e.target.value) || '' })}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select Role</option>
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={userFormData.is_active}
                  onChange={(e) => setUserFormData({ ...userFormData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditUserModal(false)}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                style={primaryButtonStyle}
              >
                Update User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateRoleModal && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 20px 0' }}>Create New Role</h3>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Role Name *</label>
              <input
                type="text"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                placeholder="e.g., support_agent"
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Description</label>
              <input
                type="text"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                placeholder="e.g., Support team members"
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Permissions (JSON)</label>
              <textarea
                value={permissionString}
                onChange={(e) => setPermissionString(e.target.value)}
                placeholder={`{
  "customers": ["read", "write"],
  "rides": ["read"],
  "support": ["read", "write"]
}`}
                style={{ ...inputStyle, minHeight: '120px', fontFamily: 'monospace', fontSize: '13px' }}
              />
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                💡 Example: {"{"}"customers": ["read", "write"], "rides": ["read"]{"}"}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateRoleModal(false)}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                style={primaryButtonStyle}
              >
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && selectedRole && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3 style={{ margin: '0 0 20px 0' }}>Edit Role: {selectedRole.name}</h3>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Role Name *</label>
              <input
                type="text"
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Description</label>
              <input
                type="text"
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                style={inputStyle}
              />
            </div>
            
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>Permissions (JSON)</label>
              <textarea
                value={permissionString}
                onChange={(e) => setPermissionString(e.target.value)}
                style={{ ...inputStyle, minHeight: '120px', fontFamily: 'monospace', fontSize: '13px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditRoleModal(false)}
                style={cancelButtonStyle}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                style={primaryButtonStyle}
              >
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== STYLES ====================

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '24px',
  width: '500px',
  maxWidth: '90%',
  maxHeight: '90vh',
  overflow: 'auto',
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  marginBottom: '4px',
  fontWeight: '500',
  fontSize: '13px',
  color: '#475569',
};

const cancelButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#e2e8f0',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  color: '#475569',
};

const primaryButtonStyle = {
  padding: '10px 20px',
  backgroundColor: '#f97316',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

export default UserManagement;